import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import type { CustomerData } from '../../stores/order-panel-store'
import clsx from 'clsx'

/**
 * Stap 1 van de native nieuwe-aanvraag: klant zoeken (bestaande accounts/leads,
 * incl. blacklist-block en B2B-contactkeuze) óf — telefoonscript — een nieuwe
 * klant die start met alléén de postcode. Prijzen/routes laden dan direct;
 * naam/contact/adres volgen pas ná de afhandelingskeuze (KlantGegevensForm).
 *
 * Bestaande klanten worden via /api/customers/detail verrijkt (search geeft
 * geen straat terug); bezorgadres heeft voorrang — zelfde logica als
 * KlantDetailView.buildOrderCustomer.
 */

interface SearchResult {
  id: string
  type: 'account_person' | 'account_business' | 'lead'
  name: string
  email: string
  phone: string
  city: string
  postcode: string
  blacklisted?: boolean
  blacklistReden?: string | null
}

interface ContactRow {
  id: string
  name: string
  email: string
  phone: string
}

interface CustomerDetailCore {
  id: string
  name: string
  firstName: string
  lastName: string
  email: string
  phone: string
  street: string
  postcode: string
  city: string
  shippingStreet: string
  shippingPostcode: string
  shippingCity: string
  isPersonAccount?: boolean
  blacklisted?: boolean
  blacklistReden?: string | null
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  const h: Record<string, string> = {}
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`
  return h
}

function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return { firstName: '', lastName: parts[0] ?? name.trim() }
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') }
}

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'

export function KlantStep({
  onCustomer,
  onNewPostcode,
  onWalkIn,
}: {
  /** Bestaande klant gekozen — gegevens compleet. */
  onCustomer: (c: CustomerData) => void
  /** Nieuwe klant — telefoonflow start met alleen de postcode. */
  onNewPostcode: (postcode: string, opts: { country: string; buitenWerkgebied: boolean }) => void
  /** Walk-in — klant brengt en haalt zelf; geen postcode, geen routes. */
  onWalkIn: () => void
}) {
  const [mode, setMode] = useState<'zoeken' | 'nieuw'>('zoeken')

  // ── Zoeken ────────────────────────────────────────────────
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null)
  const [selectError, setSelectError] = useState<string | null>(null)
  // B2B: detail + contacten geladen, wacht op contactkeuze
  const [b2bPick, setB2bPick] = useState<{
    detail: CustomerDetailCore
    contacts: ContactRow[]
    defaultContactId: string | null
  } | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      setSearching(false)
      return
    }
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setSearching(true)

    const timer = setTimeout(async () => {
      try {
        const headers = await authHeaders()
        const response = await fetch(
          `/api/customers/search?q=${encodeURIComponent(trimmed)}`,
          { headers, signal: controller.signal }
        )
        if (!response.ok) {
          setResults([])
          return
        }
        const data = await response.json()
        setResults(((data.results as SearchResult[]) || []).slice(0, 8))
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  async function fetchDetail(result: SearchResult): Promise<CustomerDetailCore | null> {
    const headers = await authHeaders()
    const type = result.type === 'lead' ? 'lead' : 'account'
    const response = await fetch(
      `/api/customers/detail?id=${encodeURIComponent(result.id)}&type=${type}`,
      { headers }
    )
    if (!response.ok) return null
    const data = await response.json()
    return (data?.customer as CustomerDetailCore) ?? null
  }

  function detailToCustomerData(
    detail: CustomerDetailCore,
    opts: { contact?: ContactRow; isLead?: boolean } = {}
  ): CustomerData {
    const { contact, isLead } = opts
    // Bezorgadres heeft voorrang voor route-lookup en order-aanmaak (zelfde
    // gedrag als klant-detail; SF vult Shipping auto-fill = Billing).
    const street = detail.shippingStreet || detail.street
    const postcode = detail.shippingPostcode || detail.postcode
    const city = detail.shippingCity || detail.city

    // Een lead heeft géén Account: detail.id is dan een Lead-Id (00Q...). Dat
    // in accountId zetten laat create.ts het als Existing_Account__c (001...)
    // meesturen en SF weigert de Lead met FIELD_INTEGRITY_EXCEPTION. De
    // leadgegevens dienen hier alleen als prefill; AccountResolver/converter
    // matcht bij conversie zelf op e-mail. Zelfde keuze als NewOfferteDialog.
    const accountId = isLead ? undefined : detail.id

    if (contact) {
      const { firstName, lastName } = splitName(contact.name)
      return {
        accountId,
        firstName,
        lastName,
        email: contact.email || detail.email,
        phone: contact.phone || detail.phone,
        street,
        postcode,
        city,
        companyName: detail.name,
        contactId: contact.id,
      }
    }

    const fallback = splitName(detail.name)
    return {
      accountId,
      firstName: detail.firstName || fallback.firstName,
      lastName: detail.lastName || fallback.lastName,
      email: detail.email,
      phone: detail.phone,
      street,
      postcode,
      city,
    }
  }

  async function selectResult(result: SearchResult) {
    if (result.blacklisted) return
    setSelectError(null)
    setLoadingDetailId(result.id)
    try {
      const detail = await fetchDetail(result)
      if (!detail) {
        setSelectError('Klantgegevens laden mislukt — probeer opnieuw.')
        return
      }
      if (detail.blacklisted) {
        setSelectError(`Klant staat op de blacklist${detail.blacklistReden ? ` — ${detail.blacklistReden}` : ''}.`)
        return
      }
      if (result.type === 'account_business') {
        // B2B: contactpersoon kiezen vóór we doorgaan
        const headers = await authHeaders()
        const response = await fetch(
          `/api/customers/contacts-by-account?id=${encodeURIComponent(result.id)}`,
          { headers }
        )
        const data = response.ok
          ? ((await response.json()) as { contacts?: ContactRow[]; defaultContactId?: string | null })
          : {}
        setB2bPick({
          detail,
          contacts: data.contacts ?? [],
          defaultContactId: data.defaultContactId ?? null,
        })
        return
      }
      if (result.type === 'lead' && !detail.postcode) {
        setSelectError('Deze lead heeft geen postcode — vul de klant handmatig in via "Nieuwe klant".')
        return
      }
      onCustomer(detailToCustomerData(detail, { isLead: result.type === 'lead' }))
    } finally {
      setLoadingDetailId(null)
    }
  }

  // ── Nieuw — telefoonscript: alleen postcode ───────────────
  // Land bepaalt het postcodeformaat. BE/DE = automatisch buiten werkgebied
  // (handmatig inplannen + toeslag) — een numerieke BE/DE-postcode zou anders
  // vals matchen op een NL-gebied. Voor NL bepaalt de postcode het zelf: valt
  // hij buiten alle dienstgebieden, dan biedt de volgende stap de uitzondering
  // aan (buitenWerkgebiedDetected in de store).
  const [land, setLand] = useState<'NL' | 'BE' | 'DE'>('NL')
  const [postcode, setPostcode] = useState('')
  const postcodeClean = postcode.replace(/\s+/g, '').toUpperCase()
  const postcodeValid =
    land === 'NL' ? /^\d{4}[A-Z]{2}$/.test(postcodeClean)
    : land === 'BE' ? /^\d{4}$/.test(postcodeClean)
    : /^\d{5}$/.test(postcodeClean)
  const buitenWerkgebied = land !== 'NL'
  const landNaam = land === 'BE' ? 'België' : land === 'DE' ? 'Duitsland' : 'Nederland'
  const startNieuw = () => {
    if (!postcodeValid) return
    onNewPostcode(postcodeClean, { country: landNaam, buitenWerkgebied })
  }

  // ── B2B contactkeuze-overlay ──────────────────────────────
  if (b2bPick) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{b2bPick.detail.name}</h3>
            <p className="text-xs text-slate-500">Kies de contactpersoon voor deze aanvraag</p>
          </div>
          <button
            onClick={() => setB2bPick(null)}
            className="text-xs text-indigo-600 hover:underline"
          >
            Terug naar zoeken
          </button>
        </div>
        {b2bPick.contacts.length === 0 ? (
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
            Geen contactpersonen onder dit account. Voeg eerst een contactpersoon toe op de klantpagina.
          </div>
        ) : (
          <div className="space-y-2">
            {b2bPick.contacts.map((c) => (
              <button
                key={c.id}
                onClick={() => onCustomer(detailToCustomerData(b2bPick.detail, { contact: c }))}
                className={clsx(
                  'w-full text-left px-3 py-2.5 border rounded-md transition-colors hover:border-indigo-400',
                  c.id === b2bPick.defaultContactId
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-slate-200 bg-white'
                )}
              >
                <div className="text-sm font-medium text-slate-900">
                  {c.name}
                  {c.id === b2bPick.defaultContactId && (
                    <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                      Standaard
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {[c.email, c.phone].filter(Boolean).join(' / ') || 'Geen contactgegevens'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2">
        {([
          { value: 'zoeken', label: 'Bestaande klant' },
          { value: 'nieuw', label: 'Nieuwe klant' },
        ] as const).map((m) => (
          <button
            key={m.value}
            onClick={() => setMode(m.value)}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded-md border transition-colors',
              mode === m.value
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'zoeken' && (
        <div className="space-y-3">
          <input
            type="text"
            autoFocus
            placeholder="Zoek op naam, e-mail, telefoon, postcode of WO-nummer..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={inputClass}
          />

          {selectError && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {selectError}
            </div>
          )}

          {searching && (
            <div className="space-y-2 animate-pulse" role="status" aria-label="Zoeken">
              <div className="h-14 bg-slate-100 rounded-md" />
              <div className="h-14 bg-slate-100 rounded-md" />
            </div>
          )}

          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div className="px-3 py-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600 text-center">
              Geen klanten gevonden — maak een{' '}
              <button onClick={() => setMode('nieuw')} className="text-indigo-600 hover:underline">
                nieuwe klant
              </button>{' '}
              aan.
            </div>
          )}

          <div className="space-y-2">
            {results.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => selectResult(r)}
                disabled={r.blacklisted || loadingDetailId !== null}
                className={clsx(
                  'w-full text-left px-3 py-2.5 border rounded-md transition-colors',
                  r.blacklisted
                    ? 'border-red-200 bg-red-50 cursor-not-allowed'
                    : 'border-slate-200 bg-white hover:border-indigo-400',
                  loadingDetailId === r.id && 'opacity-60'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">{r.name}</span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">
                    {r.type === 'lead' ? 'Lead' : r.type === 'account_business' ? 'Zakelijk' : 'Particulier'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {[r.email, r.phone, [r.postcode, r.city].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                {r.blacklisted && (
                  <div className="mt-1 text-xs font-medium text-red-700">
                    ⛔ Blacklist{r.blacklistReden ? ` — ${r.blacklistReden}` : ''}
                  </div>
                )}
                {loadingDetailId === r.id && (
                  <div className="mt-1 text-xs text-slate-500">Gegevens laden...</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === 'nieuw' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Vraag de postcode — dan staan de prijzen en ophaalmomenten direct klaar.
            Naam en adres komen pas bij het afronden.
          </p>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Land</label>
            <div className="flex gap-2">
              {([['NL', 'Nederland'], ['BE', 'België'], ['DE', 'Duitsland']] as const).map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => { setLand(code); setPostcode('') }}
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-md border transition-colors',
                    land === code
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Postcode *</label>
            <input
              type="text"
              autoFocus
              placeholder={land === 'NL' ? '1234 AB' : land === 'BE' ? '2321' : '48599'}
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') startNieuw()
              }}
              className={inputClass}
            />
          </div>
          {buitenWerkgebied && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
              Buiten werkgebied ({landNaam}): toeslag wordt toegepast op de prijs,
              geen automatische ophaalmomenten — inplannen gaat na aanmaak handmatig
              via de order.
            </div>
          )}
          <button
            onClick={startNieuw}
            disabled={!postcodeValid}
            className={clsx(
              'w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
              postcodeValid
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            )}
          >
            Prijzen ophalen
          </button>

          {/* Walk-in: geen postcode nodig. Prijzen draaien op het wasserij-eigen
              gebied (geen regiotoeslag) en er worden geen ritten gepland. */}
          <div className="flex items-center gap-3 pt-1">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">of</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            type="button"
            onClick={onWalkIn}
            className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors"
          >
            Klant brengt en haalt zelf
          </button>
          <p className="text-xs text-slate-500 -mt-1">
            Geen postcode nodig: standaard wasserij-prijzen zonder regiotoeslag,
            geen ophaal- of bezorgrit. Alleen naam, e-mail en telefoon zijn nodig.
          </p>
        </div>
      )}
    </div>
  )
}
